import * as CSS from 'csstype';
import { colors } from '../../settings';
import { ButtonColorConfig, ColorCombo } from './types';

const defaultColorCombo: ButtonColorConfig = {
    normal: {
        backgroundColor: 'grey900',
        backgroundColorHover: 'grey700',
        backgroundColorActive: 'grey600',
        borderColor: 'grey900',
        borderColorHover: 'grey700',
        borderColorActive: 'grey600',
        textColor: 'white',
        textColorHover: 'white',
        textColorActive: 'white',
    },
    selected: {
        backgroundColor: 'grey600',
        backgroundColorHover: 'grey800',
        backgroundColorActive: 'grey700',
        borderColor: 'grey600',
        borderColorHover: 'grey800',
        borderColorActive: 'grey700',
        textColor: 'white',
        textColorHover: 'white',
        textColorActive: 'white',
    },
    disabled: {
        backgroundColor: 'grey200',
        backgroundColorHover: 'grey200',
        backgroundColorActive: 'grey200',
        borderColor: 'grey200',
        borderColorHover: 'grey200',
        borderColorActive: 'grey200',
        textColor: 'grey400',
        textColorHover: 'grey400',
        textColorActive: 'grey400',
    },
};

export function createButtonColorConfig(
    overrides: Partial<{
        normal?: Partial<ColorCombo>;
        selected?: Partial<ColorCombo>;
        disabled?: Partial<ColorCombo>;
    }> = {
        selected: undefined,
        disabled: undefined,
        normal: undefined,
    }
): ButtonColorConfig {
    const { normal = {}, disabled = {}, selected = {} } = overrides;
    return {
        normal: { ...defaultColorCombo.normal, ...normal },
        selected: { ...defaultColorCombo.selected, ...selected },
        disabled: { ...defaultColorCombo.disabled, ...disabled },
    };
}

export function createButtonPrimaryColorConfig(params: {
    color: CSS.Property.Color | colors.Color;
    colorHover: CSS.Property.Color | colors.Color;
    colorActive: CSS.Property.Color | colors.Color;
}): ButtonColorConfig {
    const { color, colorHover, colorActive } = params;
    return createButtonColorConfig({
        normal: {
            backgroundColor: color,
            backgroundColorHover: colorHover,
            backgroundColorActive: colorActive,
            borderColor: color,
            borderColorHover: colorHover,
            borderColorActive: colorActive,
        },
    });
}

export function createButtonSecondaryColorConfig(): ButtonColorConfig {
    return createButtonColorConfig({
        normal: {
            backgroundColor: 'grey300',
            backgroundColorHover: 'grey400',
            backgroundColorActive: 'grey500',
            borderColor: 'grey300',
            borderColorHover: 'grey400',
            borderColorActive: 'grey500',
            textColor: 'grey900',
            textColorHover: 'grey900',
            textColorActive: 'grey900',
        },
    });
}

export function createButtonTertiaryColorConfig(params: {
    color: CSS.Property.Color | colors.Color;
    colorHover: CSS.Property.Color | colors.Color;
    colorActive: CSS.Property.Color | colors.Color;
}): ButtonColorConfig {
    const { color, colorHover, colorActive } = params;
    return createButtonColorConfig({
        normal: {
            backgroundColor: 'transparent',
            backgroundColorHover: 'transparent',
            backgroundColorActive: 'transparent',
            borderColor: color,
            borderColorHover: colorHover,
            borderColorActive: colorActive,
            textColor: color,
            textColorHover: colorHover,
            textColorActive: colorActive,
        },
        disabled: {
            backgroundColor: 'transparent',
            backgroundColorHover: 'transparent',
            backgroundColorActive: 'transparent',
            borderColor: 'grey200',
            borderColorHover: 'grey200',
            borderColorActive: 'grey200',
            textColor: 'grey400',
            textColorHover: 'grey400',
            textColorActive: 'grey400',
        },
    });
}

export function createButtonTransparentColorConfig(params: {
    color: CSS.Property.Color | colors.Color;
    colorHover: CSS.Property.Color | colors.Color;
    colorActive: CSS.Property.Color | colors.Color;
}): ButtonColorConfig {
    const { color, colorHover, colorActive } = params;
    return createButtonColorConfig({
        normal: {
            backgroundColor: 'transparent',
            backgroundColorHover: 'transparent',
            backgroundColorActive: 'transparent',
            borderColor: 'transparent',
            borderColorHover: 'transparent',
            borderColorActive: 'transparent',
            textColor: color,
            textColorHover: colorHover,
            textColorActive: colorActive,
        },
        selected: {
            backgroundColor: 'transparent',
            backgroundColorHover: 'transparent',
            backgroundColorActive: 'transparent',
            borderColor: 'transparent',
            borderColorHover: 'transparent',
            borderColorActive: 'transparent',
            textColor: 'grey800',
            textColorHover: 'grey800',
            textColorActive: 'grey900',
        },
        disabled: {
            backgroundColor: 'transparent',
            backgroundColorHover: 'transparent',
            backgroundColorActive: 'transparent',
            borderColor: 'transparent',
            borderColorHover: 'transparent',
            borderColorActive: 'transparent',
            textColor: 'grey400',
            textColorHover: 'grey400',
            textColorActive: 'grey400',
        },
    });
}
